import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import UploadCloudFillIcon from "remixicon-react/UploadCloudFillIcon";
import {
  createTag,
  updateTag,
} from "../../../core/actionCreators/productTagsActionCreator";
import { uploadProductTagImage } from "../../../core/services/productTagsService";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import TagMsnsPopup from "../../components/Popup/TagMsnsPopup";

const MSN_PREVIEW_COUNT = 5;

const tagFormSchema = Yup.object().shape({
  tagName: Yup.string()
    .min(2, "Tag name is too Short!")
    .max(15, "Tag name is too Long!")
    .required("Tag name is Required"),
  priority: Yup.number()
    .max(50, "Priority cannot be greater than 50")
    .required(),
  description: Yup.string()
    .min(5, "description is  too Short!")
    .max(30, "description is  too Long!")
    .required("description is Required"),
  type: Yup.string().required("Type is required"),
  status: Yup.string().required("Status is required"),
  addes_msn: Yup.string(),
  delete_msn: Yup.string(),
});

interface CreateNewTagProps {
  productTagDetails: any;
  pageMode: string;
}
const CreateNewTag: React.FC<CreateNewTagProps> = ({
  productTagDetails,
  pageMode,
}) => {
  // An existing tag opens locked and is unlocked with Edit; a new one is
  // editable straight away. (This was `editMode`, which confusingly meant the
  // opposite — true disabled every field.)
  const [isReadOnly, setIsReadOnly] = useState(pageMode !== "create");
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState("");
  const [tagMsns, setTagMsns] = useState([]);
  const [file, setFile] = useState("");
  const [showImage, setShowImage] = useState("");
  const [showMsnsPopup, setShowMsnsPopup] = useState(false);

  useEffect(() => {
    if (productTagDetails && pageMode === "edit") {
      setImageUrl(productTagDetails.imageUrl);
      setShowImage(productTagDetails.imageUrl);
      setTagMsns(productTagDetails.products);
    }
  }, [productTagDetails, pageMode]);

  const editTagInfo = () => {
    setIsReadOnly(false);
  };

  const fileHandler = (e: any) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      let file = e.target.files[0];
      setFile(file);
      setImageUrl("");
      setShowImage(URL.createObjectURL(e.target.files[0]));
    }
  };

  const imageUploadHandler = async () => {
    if (!file) {
      return null;
    }
    const formData = new FormData();
    formData.append("file", file);
    const data = await uploadProductTagImage(formData);
    return data?.data?.data;
  };

  const getPostBody = async (data: any, imageUrl: any) => {
    return {
      name: data.tagName,
      type: data.type,
      description: data.description,
      priority: data.priority.toString(),
      status: data.status,
      imageUrl: imageUrl,
      addProducts: data.addes_msn.length > 0 ? data.addes_msn.split(",") : [],
      removeProducts:
        data.delete_msn.length > 0 ? data.delete_msn.split(",") : [],
    };
  };

  const checkIsDataUpdated = (fields: any, productTagDetails: any)=>{
    if(
      productTagDetails != null &&
      fields['tagName'] == productTagDetails['name'] &&
      fields['description'] == productTagDetails['description'] && 
      fields['type'] == productTagDetails['type'] && 
      fields['priority'].toString() == productTagDetails['priority'] && 
      fields['status'] == productTagDetails['status'] 
    ){ return null }else{
      return "done";
    }
  }

  const onSubmit = async (fields: any, { setStatus, setSubmitting }: any) => {
    // const isDone =  checkIsDataUpdated(fields, productTagDetails);
    // if(isDone == null){ 
    //   toast.error("Please make some changes in data.");
    //   return; 
    // }
    setSubmitting(true);
    if (pageMode === "create") {
      const response = await imageUploadHandler();
      if (response === null) {
        toast.error("Please select image.");
        setSubmitting(false);
        return;
      }
      const resquestBody = await getPostBody(fields, response);
      dispatch(
        createTag(resquestBody, (response: any) => {
          setSubmitting(false);
          if (response.status) {
            toast.success("Created successfully.");
          } else {
            toast.error("Something went wrong.");
          }
        })
      );
    } else if (pageMode === "edit") {
      // A newly picked file has to be uploaded first; an untouched tag keeps
      // the image URL it already had.
      let resolvedImageUrl = imageUrl;
      if (imageUrl?.length === 0 || imageUrl === null) {
        const uploaded = await imageUploadHandler();
        if (uploaded === null) {
          toast.error("Please select image.");
          setSubmitting(false);
          return;
        }
        resolvedImageUrl = uploaded;
      }

      const resquestBody = await getPostBody(fields, resolvedImageUrl);
      dispatch(
        updateTag(
          { ...resquestBody, tagId: productTagDetails.tagId },
          (response: any) => {
            setSubmitting(false);
            if (response.status) {
              toast.success("Updated successfully.");
              // The save landed — put the form back to read-only so Edit shows
              // again instead of leaving Cancel/Save up.
              setIsReadOnly(true);
            } else {
              toast.error("Something went wrong.");
            }
          }
        )
      );
    }
  };

  // form data with initial values
  let initialValues = {};

  if (pageMode === "edit" && Object.entries(productTagDetails).length > 0) {
    initialValues = Object.assign(
      {},
      {
        tagName: productTagDetails.name,
        type: productTagDetails.type,
        description: productTagDetails.description,
        priority: productTagDetails.priority,
        status: productTagDetails.status,
        addes_msn: "",
        delete_msn: "",
      }
    );
  } else {
    initialValues = Object.assign(
      {},
      {
        tagName: "",
        type: "text",
        description: "",
        priority: "",
        status: "active",
        addes_msn: "",
        delete_msn: "",
      }
    );
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={tagFormSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, setFieldValue }) => {
        return (
          <Form>
            <div className="bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded w-full">
              <div className="flex items-center justify-between bg-[#f6f8f8] p-4 border-b border-b-[#efeff4]">
                <p className="text-base text-[#43425D]">
                  {/* The create page already names itself in its own header. */}
                  {pageMode === "create"
                    ? "Tag Details"
                    : productTagDetails?.name
                      ? `${productTagDetails.name} Tag`
                      : "Tag Details"}
                </p>
                {!isReadOnly ? (
                  <div className="flex items-center gap-3">
                    {pageMode === "edit" && (
                      <button
                        type="button"
                        onClick={() => setIsReadOnly(true)}
                        className="h-9 px-4 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => editTagInfo()}
                    className="h-9 px-4 rounded border border-[#4688F1] text-[#4688F1] text-sm font-medium hover:bg-blue-50"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="py-4 px-2.5">
                <div className="flex">
                  <div className="w-1/5 px-2">
                    <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                      Tag Image
                    </label>
                    <div className="w-[116px] h-11 p-2.5 border border-[#EAEAEA] rounded flex items-center justify-center bg-white">
                      {showImage ? (
                        <img src={showImage} alt="Tag" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <span className="text-[10px] text-[#858585]">No image</span>
                      )}
                    </div>
                    {/* Only an actionable control once the form is unlocked —
                        a permanently greyed-out button is just noise. */}
                    {!isReadOnly && (
                    <label
                      className="w-[116px] flex items-center justify-center bg-[#5D5D5D] text-white p-2.5 text-xs rounded mt-4 cursor-pointer hover:bg-[#4a4a4a]"
                    >
                      <input
                        onChange={fileHandler}
                        type="file"
                        accept="image/*"
                        className="hidden"
                      />
                      <UploadCloudFillIcon size={16} />
                      <span className="ml-1">Upload Image</span>
                    </label>
                    )}
                  </div>
                  <div className="w-4/5">
                    <div className="flex w-full mb-5">
                      <div className="w-1/2 px-2">
                        <label className="block text-xs mb-1.5 text-[#303030]">
                          Tag Name*
                        </label>
                        <Field
                          className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm focus:border-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          type="text"
                          name="tagName"
                          disabled={isReadOnly}
                        />
                        <ErrorMessage
                          name="tagName"
                          component="div"
                          className="text-xs text-[red]"
                        />
                      </div>
                      <div className="w-1/2 px-2">
                        <label className="block text-xs mb-1.5 text-[#303030]">
                          Select Tag Type*
                        </label>
                        <Field
                          name="type"
                          as="select"
                          disabled={isReadOnly}
                          className="w-full h-9 rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white focus:border-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="text">Text</option>
                          <option value="image">Image</option>
                        </Field>
                        <ErrorMessage
                          name="type"
                          component="div"
                          className="text-xs text-[red]"
                        />
                      </div>
                    </div>
                    <div className="flex w-full mb-5">
                      <div className="w-1/2 px-2">
                        <label className="block text-xs mb-1.5 text-[#303030]">
                          Tag Description*
                        </label>
                        <Field
                          className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm focus:border-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          name="description"
                          disabled={isReadOnly}
                          type="text"
                        />
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-xs text-[red]"
                        />
                      </div>
                      <div className="w-1/2 px-2">
                        <label className="block text-xs mb-1.5 text-[#303030]">
                          Priority*
                        </label>
                        <Field
                          className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm focus:border-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          name="priority"
                          disabled={isReadOnly}
                          type="number"
                        />
                        <ErrorMessage
                          name="priority"
                          component="div"
                          className="text-xs text-[red]"
                        />
                      </div>
                    </div>
                    <div className="flex w-full mb-5">
                      <div className="w-1/2 px-2">
                        <label className="block text-xs mb-1.5 text-[#303030]">
                          Status*
                        </label>
                        <Field
                          name="status"
                          as="select"
                          disabled={isReadOnly}
                          className="w-full h-9 rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white focus:border-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Field>
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="text-xs text-[red]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-2 mt-5">
                  <div className={`border border-[#EAEAEA] rounded p-4 bg-[#f8faff] ${pageMode === "create" ? "" : "mb-5"}`}>
                    <p className="text-sm text-[#303030]">
                      {pageMode === "create" ? "Add MSN" : `Added MSN (${tagMsns?.length || 0})`}
                    </p>
                    <p className="text-[11px] text-[#858585] font-light mt-0.5">Comma separated — these MSNs get the tag on save</p>
                    <div className="mt-4">
                      <Field
                        type="text"
                        name="addes_msn"
                        placeholder="e.g. MSN9876543HG, MSN9876543HG"
                        disabled={isReadOnly}
                        className="w-full h-9 rounded border border-[#EAEAEA] px-2.5 outline-none focus:border-blue-400 text-sm text-[#555] font-light disabled:bg-gray-100"
                      />
                      {/* A tag can hold thousands of MSNs — only a preview goes
                          inline, the rest live behind the popup. */}
                      {tagMsns?.length > 0 && (
                        <div className="mt-4 flex flex-wrap items-center">
                          {tagMsns.slice(0, MSN_PREVIEW_COUNT).map((item: any, index: any) => (
                            <span
                              className="bg-[#CEE1FD] text-xs px-3 py-2 rounded-3xl text-center mr-2.5 mb-2.5 text-[#3A3A3A]"
                              key={index}
                            >
                              {item}
                            </span>
                          ))}
                          {tagMsns.length > MSN_PREVIEW_COUNT && (
                            <button
                              type="button"
                              onClick={() => setShowMsnsPopup(true)}
                              className="text-xs px-3 py-2 mr-2.5 mb-2.5 text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              View all ({tagMsns.length})
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* A tag being created has nothing tagged yet, so there is
                      nothing to remove from it. */}
                  {pageMode !== "create" && (
                    <div className="border border-[#EAEAEA] rounded p-4 bg-[#f8faff]">
                      <p className="text-sm text-[#303030]">Delete MSN</p>
                      <p className="text-[11px] text-[#858585] font-light mt-0.5">Comma separated — these MSNs lose the tag on save</p>
                      <div className="mt-4">
                        <Field
                          type="text"
                          name="delete_msn"
                          placeholder="e.g. MSN9876543HG, MSN9876543HG"
                          disabled={isReadOnly}
                          className="w-full h-9 rounded border border-[#EAEAEA] px-2.5 outline-none focus:border-blue-400 text-sm text-[#555] font-light disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {showMsnsPopup && (
              <TagMsnsPopup
                msns={tagMsns || []}
                tagName={productTagDetails?.name}
                onClose={() => setShowMsnsPopup(false)}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
export default CreateNewTag;
